<template>
  <div
    class="product-carousel"
    :class="{
      ['product-carousel--cart-drawer']: placement === 'cart-drawer',
    }"
  >
    <div
      ref="carouselContainer"
      class="
        product-carousel__carousel
        splide
      "
      :js-carousel="`wrapper-${namespace}`"
    >
      <div class="splide__track">
        <ul class="splide__list">
          <li
            v-for="(product, index) in products"
            :key="product.id || index"
            class="splide__slide"
          >
            <product-card
              :index="index"
              namespace="carousel"
              :image-order="productCardImageOrder"
              :image-use-alt="productCardUseAltImage"
              :product="product"
              :show-quick-add="!hideQuickAdd"
              :placement="placement"
              @product-card-clicked="$emit('product-card-clicked', product)"
            />
          </li>
        </ul>
      </div>

      <!-- Carousel Arrows -->
      <div
        class="product-carousel__arrows splide__arrows"
        :style="`top: ${arrowTopValue}`"
      >
        <button
          v-trigger-icon
          class="splide__arrow splide__arrow--prev"
          :class="{
            [$class('disabled')]: carouselIndex === 0 || !showArrows.left
          }"
          type="button"
          :aria-label="$string('accessibility.carousel.previous')"
        >
          <animated-icon :transition-on-hover="false">
            <template #iconOne>
              <icon-arrow-left />
            </template>

            <template #iconTwo>
              <icon-arrow-left />
            </template>
          </animated-icon>
        </button>

        <button
          v-trigger-icon
          class="splide__arrow splide__arrow--next"
          :class="{
            [$class('disabled')]: (carouselIndex >= (products?.length - carouselOffset)) || !showArrows.right
          }"
          type="button"
          :aria-label="$string('accessibility.carousel.next')"
        >
          <animated-icon :transition-on-hover="false">
            <template #iconOne>
              <icon-arrow-right />
            </template>

            <template #iconTwo>
              <icon-arrow-right />
            </template>
          </animated-icon>
        </button>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product carousel (product-carousel)
 * -----------------------------------------------------------------------------
 * Carousel of product cards, added manually via settings or from a recommendation engine.
 *
 * @param {Number} autoplaySpeed - Number representation of autoplay speed in s.
 * @param {String} namespace - A unique namespace for product carousel elements. Used by splide.
 * @param {Array} products - Array of product options to render in the carousel.
 *
 */
import { nextTick } from 'vue'
import { mapState } from 'vuex'

import Carousel from "~/api/carousel"

import AnimatedIcon from '~global/animated-icon/animated-icon'
import ProductCard from "~async/product-card/product-card"

import IconArrowRight from '~icons/direction/arrow/right.svg'
import IconArrowLeft from '~icons/direction/arrow/left.svg'

export default {
  name: 'ProductCarousel',

  components: {
    AnimatedIcon,
    ProductCard,
    IconArrowRight,
    IconArrowLeft,
  },

  props: {
    autoplaySpeed: {
      type: Number,
      default: 2.5,
    },
    hideQuickAdd: {
      type: Boolean,
      default: false,
    },
    placement: {
      type: String,
      default: '',
    },
    namespace: {
      type: String,
      default: '',
    },
    productCardImageOrder: {
      type: String,
      default: '',
    },
    productCardUseAltImage: {
      type: Boolean,
      default: false,
    },
    products: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['product-card-clicked'],

  data() {
    return {
      arrowTopValue: '50%',
      carousel: null,
      carouselIndex: 0,
      showArrows: {
        left: false,
        right: false,
      },
      splideOptions: {
        perPage: 4,
        perMove: 1,
        gap: 'var(--spacing-3xs)',
      },
      splideOptionsMobile: {
        perPage: 1,
        padding: { left: '0', right: '40%' },
        perMove: 1,
        gap: 'var(--spacing-3xs)',
        drag: 'free',
        snap: true,
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      breakpoint: (state) => state.index.screen.breakpoint,
    }),

    /**
     * Grabs the current namespace.
     * @returns {String}
     */
    carouselNamespace(){
      return this.namespace ? `[js-carousel="wrapper-${this.namespace}"]` : false
    },

    /**
     * Sets a breakpoint dependent offset at which to disable the next arrow.
     */
    carouselOffset(){
      return this.isMobile || this.placement === 'cart-drawer' ? 1 : 4
    },
  },

  watch: {

    /**
     * When products update, rebuild the carousel.
     */
    products:{
      handler(){
        if (!this.products.length) {
          return
        }

        if (!this.carousel) {
          this.createCarousel()
          return
        }

        this.carousel.destroy()
        this.createCarousel()
      },
      immediate: true,
    },
  },

  methods: {

    /**
     * Sets the element width a variable for use in teh stylesheet.
     */
    async getCarouselImageHeight() {
      const wrapper =
        this.$el.querySelector(
          '.product-card__image-container',
        )

      let height

      await new Promise((resolve) => {
        const cardInterval = window.setInterval(() => {

          height = wrapper?.offsetHeight
          if (!height) {
            return
          }

          window.clearInterval(cardInterval)
          resolve()
        }, this.$timing("quick"))
      })

      this.arrowTopValue = `${height / 2}px`
      this.showArrows.left = true
      this.showArrows.right = true
    },

    /**
     * Main carousel constructor function
     */
    createCarousel() {
      if(!this.namespace){
        return
      }

      nextTick(() => {
        const container = this.$refs.carouselContainer

        if (!container) {
          return
        }

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const interval = Number(this.autoplaySpeed) * 1000
        const autoplay = Boolean(interval && !reducedMotion)

        let options = this.isMobile ? {
          perPage: 2,
          padding: { left: '16px', right: '24px' },
          perMove: 1,
          gap: 'var(--spacing-3xs)',
          drag: 'free',
          snap: true,
        } : {
          perPage: 3,
          padding: { left: '24px', right: '40px' },
          perMove: 1,
          gap: 'var(--spacing-3xs)',
          drag: 'free',
          snap: true,
        }

        if (this.placement !== 'cart-drawer') {
          options = this.isMobile && (this.breakpoint === 's' || this.breakpoint === 'xs') ? this.splideOptionsMobile : this.splideOptions
        }

        this.carousel = Carousel(this.carouselNamespace, {
          ...options,
          arrows: true,
          type: 'slide',
          rewind: true,
          autoplay,
          interval,
        })

        /**
         * When a slide updates, save the index to data.
         */
        this.carousel.on('move', (index) => {
          this.carouselIndex = index
        })

        /**
         * When a slide updates, save the index to data.
         */
        this.carousel.on('dragged', () => {
          this.carouselIndex = this.carousel.Components.Controller.getIndex()
        })

        /**
         * Wait til the carousel mounts to calculate the image height.
         */
        this.carousel.on('mounted', () => {
          setTimeout(() => {
            this.getCarouselImageHeight()
          }, this.$timing('slow'))
        })

        /**
         * Recalculate when the carousel is resized, otherwise the arrows could
         * be off center.
         */
        this.carousel.on('resized', () => {
          this.getCarouselImageHeight()
        })
        this.carousel.mount()
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-carousel';
</style>
